import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { ReactElement, useCallback } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';

export interface QualificationForm {
  code: string;
  name: string;
  lastUpdate?: string;
}

const validationSchema = yup.object({
  code: yup.string().required('Il campo è obbligatorio'),
  name: yup.string().required('Il campo è obbligatorio'),
});

interface FormQualificationProps {
  onOk: (form?: QualificationForm) => void;
  onCancel: (form?: QualificationForm) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  mode?: 'DETAIL' | 'INSERT' | 'EDIT';
  initialValues?: QualificationForm;
}

const FormQualification = ({
  onOk,
  onCancel,
  title,
  children,
  disabled = false,
  initialValues,
  mode = 'INSERT',
}: FormQualificationProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formQualification = useFormik({
    initialValues: initialValues || {
      code: '',
      name: '',
      lastUpdate: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const isInEditMode = () => mode === 'EDIT';

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formQualification.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <div>
                  <PmiTextField
                    aria-label="Codice Qualifica"
                    label="Cd Qualifica *"
                    id="code"
                    name="code"
                    type="text"
                    variant="outlined"
                    inputProps={{
                      maxLength: '2',
                    }}
                    value={formQualification.values.code || ''}
                    disabled={disabled || isInEditMode()}
                    fullWidth
                    onChange={formQualification.handleChange}
                    error={formQualification.touched.code && Boolean(formQualification.errors.code)}
                    helperText={formQualification.errors.code}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="nome"
                  label="Nome *"
                  id="name"
                  name="name"
                  inputProps={{
                    maxLength: '50',
                  }}
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={formQualification.values.name || ''}
                  disabled={disabled}
                  onChange={formQualification.handleChange}
                  error={formQualification.touched.name && Boolean(formQualification.errors.name)}
                  helperText={formQualification.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="contesto ultimo aggiornamento"
                  label="Contesto ultimo aggiornamento"
                  id="lastUpdate"
                  name="lastUpdate"
                  fullWidth
                  inputProps={{
                    maxLength: '50',
                  }}
                  variant="outlined"
                  value={formQualification.values.lastUpdate || ''}
                  onChange={formQualification.handleChange}
                  disabled={disabled}
                  error={
                    formQualification.touched.lastUpdate &&
                    Boolean(formQualification.errors.lastUpdate)
                  }
                  helperText={formQualification.errors.lastUpdate}
                />
              </Grid>
              {children ? (
                <Grid item xs={12}>
                  {children}
                </Grid>
              ) : undefined}
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(formQualification.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormQualification.displayName = 'Form Qualification component';

export default FormQualification;
